/**
 * axiosInstance.js
 *
 * Configures TWO things in one place:
 *
 * 1. A global interceptor on the default axios instance so every existing
 *    component that does `import axios from 'axios'` automatically gets the
 *    correct portal-scoped token injected — ZERO component changes needed.
 *
 * 2. A named `api` instance (pre-configured with baseURL) exported for all
 *    new code going forward.
 *
 * This file is imported once in App.js via:
 *   import './utils/axiosInstance'
 */

import axios from 'axios';
import { portalAuth } from './portalAuth';

const BASE_URL = process.env.REACT_APP_API_END_POINT;

// ─── 1. Patch the GLOBAL axios default ──────────────────────────────────────
// Every component that already does `import axios from 'axios'` will pick up
// this interceptor without any changes to the component files.

// axios.defaults.baseURL = BASE_URL;

axios.interceptors.request.use(
  (config) => {
    const token = portalAuth.getActiveToken();
    if (token) {
      // Existing components send token in a 'token' header — keep that convention.
      config.headers['token'] = token;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const portal = portalAuth.getActivePortal();
      if (portal) {
        portalAuth.logout(portal);
        // Convert camelCase portal key to kebab-case route prefix
        // e.g. 'hotelManagement' → 'hotelmanagement', 'buyerAdmin' → 'buyer-admin'
        const routePrefix = portal
          .replace(/([A-Z])/g, (m) => `-${m.toLowerCase()}`);
        window.location.href = `/${routePrefix}/login`;
      }
    }
    return Promise.reject(error);
  }
);

// ─── 2. Named `api` instance for new code ───────────────────────────────────
// Has its own interceptors (identical logic) so it works independently of
// the global axios default above.

const api = axios.create({ baseURL: BASE_URL });

api.interceptors.request.use(
  (config) => {
    const token = portalAuth.getActiveToken();
    if (token) config.headers['token'] = token;
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const portal = portalAuth.getActivePortal();
      if (portal) {
        portalAuth.logout(portal);
        const routePrefix = portal.replace(/([A-Z])/g, (m) => `-${m.toLowerCase()}`);
        window.location.href = `/${routePrefix}/login`;
      }
    }
    return Promise.reject(error);
  }
);

export default api;
